import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import {
  buildAdminWorkspaceSummary,
} from '../utils/supportSignals';

const TONES = {
  critical: 'border-red-500/30 bg-red-950/25 text-red-100',
  warning: 'border-[#fd6c00]/35 bg-[#2b1403] text-[#ffcfb0]',
  healthy: 'border-[#f5b221]/18 bg-[linear-gradient(180deg,#181512,#0b0907)] text-[#f0dfb2]',
};

export default function GlobalSignalsBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState({
    supportCases: [],
    commandAlerts: [],
    stats: {},
    notifications: [],
  });

  const loadSignals = useCallback(async () => {
    setLoading(true);
    const [supportResult, alertsResult, overviewResult, notificationsResult] =
      await Promise.allSettled([
      api.get('/admin/platform/support-cases'),
      api.get('/admin/platform/command-alerts?range=24h'),
      api.get('/admin/platform/overview'),
      api.get('/admin/platform/notifications'),
    ]);

    setPayload({
      supportCases:
        supportResult.status === 'fulfilled' ? supportResult.value.data.supportCases || [] : [],
      commandAlerts:
        alertsResult.status === 'fulfilled' ? alertsResult.value.data.alerts || [] : [],
      stats: overviewResult.status === 'fulfilled' ? overviewResult.value.data.stats || {} : {},
      notifications:
        notificationsResult.status === 'fulfilled'
          ? notificationsResult.value.data.notifications || []
          : [],
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSignals();
  }, [loadSignals, location.pathname]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadSignals();
    }, 45_000);
    return () => clearInterval(timer);
  }, [loadSignals]);

  useEffect(() => {
    const refresh = () => loadSignals();
    window.addEventListener('support-cases:updated', refresh);
    window.addEventListener('command-alerts:updated', refresh);
    window.addEventListener('admin-notifications:updated', refresh);
    return () => {
      window.removeEventListener('support-cases:updated', refresh);
      window.removeEventListener('command-alerts:updated', refresh);
      window.removeEventListener('admin-notifications:updated', refresh);
    };
  }, [loadSignals]);

  const summary = useMemo(() => buildAdminWorkspaceSummary(payload), [payload]);

  const tone = TONES[summary.severity] || TONES.healthy;

  return (
    <div className="sticky top-2 z-20 mb-4">
      <div className={`rounded-[1.8rem] border px-4 py-4 shadow-[0_14px_40px_rgba(0,0,0,0.16)] backdrop-blur ${tone}`}>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-black uppercase tracking-[0.22em]">
                Admin signals
              </p>
              {loading ? (
                <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em]">
                  Refreshing
                </span>
              ) : null}
            </div>
            <p className="mt-2 text-lg font-bold text-white">{summary.title}</p>
            <p className="mt-1 text-sm leading-6">{summary.description}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {summary.alerts.slice(0, 4).map((alert) => (
              <SignalButton
                key={alert.id}
                label={alert.count ? `${alert.count} ${alert.title}` : alert.title}
                onClick={() => navigate(alert.to)}
              />
            ))}
            <SignalButton
              label={`${summary.activeCases.length} open support`}
              onClick={() => navigate('/support')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SignalButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-white/10 bg-black/25 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:bg-black/35"
    >
      {label}
    </button>
  );
}
